<template>
    <el-scrollbar>
        <div class="overview-page">
            <!-- 标题 -->
            <div class="page-title">
                <h4>抖音高佣报表</h4>
            </div>

            <!-- 时间筛选区 -->
            <div class="filter-section shadow-card">
                <div class="filter-inner">
                    <div class="filter-left">
                        <div class="filter-header">支付时间</div>
                    </div>
                    <div class="filter-right">
                        <el-radio-group v-model="activeQuickDate" size="small" @change="selectQuickDateByKey">
                            <el-radio-button v-for="btn in quickDateButtons" :key="btn.key" :label="btn.key">{{
                                btn.label }}</el-radio-button>
                        </el-radio-group>

                        <div class="date-input-group">
                            <el-date-picker v-model="dateRange" type="daterange" range-separator="至"
                                start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd"
                                @change="onDateChange">
                            </el-date-picker>
                        </div>
                    </div>
                </div>
            </div>

            <!-- KPI卡片区 -->
            <div class="kpi-section">
                <div class="kpi-row">
                    <!-- GMV卡片 -->
                    <div class="kpi-card shadow-card gmv-card">
                        <div class="card-top">总GMV: {{ formatAmount(kpiData.gmv.totalAmount) }}</div>
                        <div class="card-content kpi-flex-row">
                            <div class="kpi-block">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">我的GMV: </span>
                                    <span class="kpi-sub-value">{{ formatAmount(kpiData.gmv.myAmount) }}</span>
                                </div>
                                <div class="donut-chart my-donut">
                                    <canvas ref="gmvMyChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.gmv.myPercentage) }}</div>
                                </div>
                            </div>
                            <div class="kpi-block kpi-block-right">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">下级GMV: </span>
                                    <span class="kpi-sub-value">{{ formatAmount(kpiData.gmv.subAmount) }}</span>
                                </div>
                                <div class="donut-chart sub-donut">
                                    <canvas ref="gmvSubChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.gmv.subPercentage) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 订单数卡片 -->
                    <div class="kpi-card shadow-card order-card">
                        <div class="card-top">总订单数: {{ kpiData.orderCount.totalCount }} 笔</div>
                        <div class="card-content kpi-flex-row">
                            <div class="kpi-block">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">我的订单数: </span>
                                    <span class="kpi-sub-value">{{ kpiData.orderCount.myAmount }} 笔</span>
                                </div>
                                <div class="donut-chart my-donut">
                                    <canvas ref="orderMyChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.orderCount.myPercentage) }}</div>
                                </div>
                            </div>
                            <div class="kpi-block kpi-block-right">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">下级订单数: </span>
                                    <span class="kpi-sub-value">{{ kpiData.orderCount.subAmount }} 笔</span>
                                </div>
                                <div class="donut-chart sub-donut">
                                    <canvas ref="orderSubChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.orderCount.subPercentage) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 佣金卡片 -->
                    <div class="kpi-card shadow-card commission-card">
                        <div class="card-top">订单总佣金: {{ formatAmount(kpiData.commission.totalAmount) }}</div>
                        <div class="card-content kpi-flex-row">
                            <div class="kpi-block">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">我的订单佣金: </span>
                                    <span class="kpi-sub-value">{{ formatAmount(kpiData.commission.myAmount) }}</span>
                                </div>
                                <div class="donut-chart my-donut">
                                    <canvas ref="commissionMyChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.commission.myPercentage) }}</div>
                                </div>
                            </div>
                            <div class="kpi-block kpi-block-right">
                                <div class="kpi-inline">
                                    <span class="kpi-sub-label">下级订单佣金: </span>
                                    <span class="kpi-sub-value">{{ formatAmount(kpiData.commission.subAmount) }}</span>
                                </div>
                                <div class="donut-chart sub-donut">
                                    <canvas ref="commissionSubChart" width="120" height="120"></canvas>
                                    <div class="donut-label">{{ getPercentage(kpiData.commission.subPercentage) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 趋势图表区 -->
            <div class="chart-section">
                <div class="chart-row">
                    <!-- GMV趋势 -->
                    <div class="chart-card shadow-card">
                        <div class="chart-header">
                            <h3>GMV趋势</h3>
                            <div class="chart-legend">
                                <span class="legend-item" :class="{ active: chartLegend.gmvTotal }"
                                    @click="toggleSeries('gmvChart', '总')">
                                    <i class="dot" :style="{ background: chartColors.gmv[1] }"></i> 总
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.gmvMy }"
                                    @click="toggleSeries('gmvChart', '我的')">
                                    <i class="dot" :style="{ background: chartColors.gmv[0] }"></i> 我的
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.gmvSub }"
                                    @click="toggleSeries('gmvChart', '下级')">
                                    <i class="dot" :style="{ background: chartColors.gmv[2] }"></i> 下级
                                </span>
                            </div>
                        </div>
                        <div class="chart-container">
                            <div ref="gmvChart" class="echarts"></div>
                        </div>
                    </div>

                    <!-- 订单数趋势 -->
                    <div class="chart-card shadow-card">
                        <div class="chart-header">
                            <h3>订单数趋势</h3>
                            <div class="chart-legend">
                                <span class="legend-item" :class="{ active: chartLegend.orderTotal }"
                                    @click="toggleSeries('orderChart', '总')">
                                    <i class="dot" :style="{ background: chartColors.order[1] }"></i> 总
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.orderMy }"
                                    @click="toggleSeries('orderChart', '我的')">
                                    <i class="dot" :style="{ background: chartColors.order[0] }"></i> 我的
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.orderSub }"
                                    @click="toggleSeries('orderChart', '下级')">
                                    <i class="dot" :style="{ background: chartColors.order[2] }"></i> 下级
                                </span>
                            </div>
                        </div>
                        <div class="chart-container">
                            <div ref="orderChart" class="echarts"></div>
                        </div>
                    </div>

                    <!-- 佣金趋势 -->
                    <div class="chart-card shadow-card">
                        <div class="chart-header">
                            <h3>佣金趋势</h3>
                            <div class="chart-legend">
                                <span class="legend-item" :class="{ active: chartLegend.commissionTotal }"
                                    @click="toggleSeries('commissionChart', '总')">
                                    <i class="dot" :style="{ background: chartColors.commission[1] }"></i> 总
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.commissionMy }"
                                    @click="toggleSeries('commissionChart', '我的')">
                                    <i class="dot" :style="{ background: chartColors.commission[0] }"></i> 我的
                                </span>
                                <span class="legend-item" :class="{ active: chartLegend.commissionSub }"
                                    @click="toggleSeries('commissionChart', '下级')">
                                    <i class="dot" :style="{ background: chartColors.commission[2] }"></i> 下级
                                </span>
                            </div>
                        </div>
                        <div class="chart-container">
                            <div ref="commissionChart" class="echarts"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 排行区：左右并列 -->
            <div class="rankings-wrapper">
                <div class="ranking-left shadow-card">
                    <div class="ranking-header">
                            <h3>商品排行榜（TOP10）</h3>
                        <div class="ranking-controls">
                            <!-- 使用右侧 el-radio-group（不显示文字标签），与标题同一行左右分布 -->
                            <el-radio-group v-model="productRankType" @change="onProductRankTypeChange" size="small">
                                <el-radio-button label="total">总</el-radio-button>
                                <el-radio-button label="my">我的</el-radio-button>
                                <el-radio-button label="sub">下级</el-radio-button>
                            </el-radio-group>
                        </div>
                    </div>
                    <el-table :data="productRankingData" stripe style="width: 100%" size="mini"
                        :row-class-name="tableRowClassName">
                        <el-table-column label="排名" type="index" width="60" align="center">
                            <template slot-scope="scope">
        <img 
          v-if="scope.$index === 0" 
          :src="medalImages.first" 
          alt="第一名" 
          width="24" 
          height="24"
        />
        <img 
          v-else-if="scope.$index === 1" 
          :src="medalImages.second" 
          alt="第二名" 
          width="24" 
          height="24"
        />
        <img 
          v-else-if="scope.$index === 2" 
          :src="medalImages.third" 
          alt="第三名" 
          width="24" 
          height="24"
        />
        <span v-else style="margin-left: 9px;">{{ scope.$index + 1 }}</span>
    </template>
                        </el-table-column>
                        <el-table-column label="商品主图" width="80">
                            <template slot-scope="scope">
                                <el-image class="product-img" v-if="scope.row.goodsThumbnailUrl"
                                    style="width: 30px; height: 30px" :src="scope.row.goodsThumbnailUrl || ''"
                                    :preview-src-list="[scope.row.goodsThumbnailUrl || '']">
                                </el-image>
                            </template>

                        </el-table-column>
                        <el-table-column label="商品名称" prop="goodsName" show-overflow-tooltip></el-table-column>
                        <el-table-column label="店铺名称" prop="shopName" width="120" show-overflow-tooltip></el-table-column>
                        <el-table-column label="GMV" prop="gmv" width="100" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onProductHeaderClick('gmv')">
                                    <span :data-active="productSortActive.prop" :class="{ 'sort-active': productSortActive.prop === 'gmv' }">GMV</span>
                                    <img class="sort-icon" :class="{ 'sort-active': productSortActive.prop === 'gmv' }" :src="getProductIcon('gmv')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                            <template slot-scope="scope">
                                {{ formatAmount(scope.row.gmv) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="订单数" prop="orderCount" width="90" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onProductHeaderClick('orderCount')">
                                    <span :data-active="productSortActive.prop" :class="{ 'sort-active': productSortActive.prop === 'orderCount' }">订单数</span>
                                    <img class="sort-icon" :class="{ 'sort-active': productSortActive.prop === 'orderCount' }" :src="getProductIcon('orderCount')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="总佣金" prop="commissionAmount" width="100" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onProductHeaderClick('commissionAmount')">
                                    <span :data-active="productSortActive.prop" :class="{ 'sort-active': productSortActive.prop === 'commissionAmount' }">总佣金</span>
                                    <img class="sort-icon" :class="{ 'sort-active': productSortActive.prop === 'commissionAmount' }" :src="getProductIcon('commissionAmount')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                            <template slot-scope="scope">
                                {{ formatAmount(scope.row.commissionAmount) }}
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div class="ranking-right shadow-card">
                    <div class="ranking-header">
                        <h3>下级排行榜（TOP10）</h3>
                    </div>
                    <el-table :data="subordinateRankingData" stripe style="width: 100%" size="mini"
                        :row-class-name="tableRowClassName">
                        <el-table-column label="排名" type="index" width="60">
                            <template slot-scope="scope">
                                <img 
          v-if="scope.$index === 0" 
          :src="medalImages.first" 
          alt="第一名" 
          width="24" 
          height="24"
        />
        <img 
          v-else-if="scope.$index === 1" 
          :src="medalImages.second" 
          alt="第二名" 
          width="24" 
          height="24"
        />
        <img 
          v-else-if="scope.$index === 2" 
          :src="medalImages.third" 
          alt="第三名" 
          width="24" 
          height="24"
        />
        <span v-else style="margin-left: 9px;">{{ scope.$index + 1 }}</span>
    </template>
                        </el-table-column>
                        <el-table-column label="渠道昵称" prop="nickName" show-overflow-tooltip></el-table-column>
                        <el-table-column label="渠道手机号" prop="mobilePhone" show-overflow-tooltip></el-table-column>
                        <el-table-column label="GMV" prop="gmv" width="120" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onSubordinateHeaderClick('gmv')">
                                    <span :data-active="subordinateSortActive.prop" :class="{ 'sort-active': subordinateSortActive.prop === 'gmv' }">GMV</span>
                                    <img class="sort-icon" :class="{ 'sort-active': subordinateSortActive.prop === 'gmv' }" :src="getSubIcon('gmv')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                            <template slot-scope="scope">
                                {{ formatAmount(scope.row.gmv) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="订单数" prop="orderCount" width="100" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onSubordinateHeaderClick('orderCount')">
                                    <span :data-active="subordinateSortActive.prop" :class="{ 'sort-active': subordinateSortActive.prop === 'orderCount' }">订单数</span>
                                    <img class="sort-icon" :class="{ 'sort-active': subordinateSortActive.prop === 'orderCount' }" :src="getSubIcon('orderCount')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="总佣金" prop="commissionAmount" width="120" align="right">
                            <template slot="header">
                                <div class="sortable-header" @click="onSubordinateHeaderClick('commissionAmount')">
                                    <span :data-active="subordinateSortActive.prop" :class="{ 'sort-active': subordinateSortActive.prop === 'commissionAmount' }">总佣金</span>
                                    <img class="sort-icon" :class="{ 'sort-active': subordinateSortActive.prop === 'commissionAmount' }" :src="getSubIcon('commissionAmount')" width="14" height="14" alt="sort"/>
                                </div>
                            </template>
                            <template slot-scope="scope">
                                {{ formatAmount(scope.row.commissionAmount) }}
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
    </el-scrollbar>
</template>

<script>
import * as echarts from 'echarts'
import dayjs from 'dayjs'
import NP from 'number-precision';
import { Loading } from 'element-ui';
// using direct src paths for images in template

export default {
    name: 'OrderManageOverview',
    data() {
        return {
            // 日期相关
            dateRange: [],
            activeQuickDate: 'today',
            quickDateButtons: [
                { key: 'today', label: '今天' },
                { key: 'yesterday', label: '昨天' },
                { key: 'week', label: '最近一周' },
                { key: 'month', label: '最近一个月' },
            ],

            boardData: {},
            kpiData: {
                gmv: { myAmount: 0, subAmount: 0, myPercentage: 0, subPercentage: 0 },
                orderCount: { myAmount: 0, subAmount: 0, myPercentage: 0, subPercentage: 0 },
                commission: { myAmount: 0, subAmount: 0, myPercentage: 0, subPercentage: 0 }
            },

            // 图表相关
            chartInstances: {},
            chartLegend: {
                gmvTotal: true,
                gmvMy: true,
                gmvSub: true,
                orderTotal: true,
                orderMy: true,
                orderSub: true,
                commissionTotal: true,
                commissionMy: true,
                commissionSub: true
            },

            // 排行数据
            productRankType: 'total',
            productMetricType: 'gmv',
            productRankingData: [],
            subordinateSortBy: 'gmv',
            subordinateRankingData: [],

            // Canvas实例
            donutCharts: {}
            ,
            // custom sort active state for product and subordinate tables
            productSortActive: { prop: '', order: 'descending' },
            subordinateSortActive: { prop: '', order: 'descending' },
            // 图表配色（深/常/浅）
            chartColors: {
                gmv: ['#8A2BE2', '#C44FC7', '#F7C8F0'],
                order: ['#0B66C3', '#4A90E2', '#BFE6FF'],
                commission: ['#5D33A8', '#8A2BE2', '#DEC1FF']
            },
            medalImages: {
            first: require('./image/top1.png'),
            second: require('./image/top2.png'),
            third: require('./image/top3.png')
        },
        }
    },

    methods: {
        formatAmount (amount) {
  if (amount === null || amount === undefined) {
    return '¥0.00';
  }
  const formatted = NP.round(amount, 2);
  return `¥${formatted.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
},
tableRowClassName({row, rowIndex}) {
        console.log('%c [ rowIndex ]-337', 'font-size:13px; background:pink; color:#bf2c9f;', rowIndex)
        if (rowIndex === 0) {
          return 'first-row';
        } else if (rowIndex === 1) {
          return 'second-row';
        } else if (rowIndex === 2) {
          return 'third-row';
        }
        return '';
      },


        getPercentage(percentage) {
            if (!percentage) return '0%'
            return (percentage * 100).toFixed(1) + '%'
        },

        // 快捷日期选择（radio 按钮）
        selectQuickDateByKey(val) {
            this.activeQuickDate = val
            const range = this.getQuickDateRange(val)
            this.dateRange = range
            this.onDateChange(range, "quick")
        },

        getQuickDateRange(type) {
            // use dayjs to ensure local-day boundaries (avoid toISOString timezone issues)
            const now = dayjs()
            // end of selected day should be the same day (date string), we only pass yyyy-MM-dd to backend
            let start = now.startOf('day')
            let end = now.startOf('day')

            switch (type) {
                case 'today':
                    // start and end are today
                    break
                case 'yesterday':
                    start = start.subtract(1, 'day')
                    end = start
                    break
                case 'week':
                    start = start.subtract(7, 'day')
                    break
                case 'month':
                    start = start.subtract(30, 'day')
                    break
                case 'quarter':
                    start = start.subtract(90, 'day')
                    break
                default:
                    return []
            }

            return [this.formatDate(start.toDate()), this.formatDate(end.toDate())]
        },

        formatDate(date) {
            // accept Date or dayjs
            try {
                return dayjs(date).format('YYYY-MM-DD')
            } catch (e) {
                return ''
            }
        },

        // 日期改变事件
        async onDateChange(dateRange, type = "picker") {
            // When user manually selects via date-picker, clear the quick-select radio
            if (type === "picker") {
                this.activeQuickDate = ''
            }
            if (!dateRange || dateRange.length !== 2) return
            await this.fetchBoardData(dateRange[0], dateRange[1])
        },

        // 获取看板数据
        async fetchBoardData(beginDate, endDate) {
            let loadingInstance = Loading.service({ fullscreen: true, text: '正在玩命加载中...' });
            try {
                const params = { beginDate, endDate }
                const resp = await this.$http('get', 'mall/bdOrderReportBoard/getReportBoardData', params)
                console.log('%c [ resp ]-399', 'font-size:13px; background:pink; color:#bf2c9f;', resp)

                // 根据拦截器实现，resp 可能已经是 data 或者包含 data 字段
                var payload = null
                if (!resp) {
                    payload = null
                } else if (typeof resp === 'object' && typeof resp.data !== 'undefined') {
                    // axios 原始响应或拦截器未完全解包
                    payload = resp.data
                } else {
                    // 拦截器已经返回了内部 data 或直接的业务对象
                    payload = resp
                }

                // 如果 payload 包含最外层的 { errCode, errMsg, data }
                if (payload && typeof payload.data !== 'undefined' && (typeof payload.errCode === 'number' || payload.errCode === 0)) {
                    this.boardData = payload.data
                } else {
                    this.boardData = payload || {}
                }

                console.log('fetchBoardData -> boardData:', this.boardData)

                // 渲染和处理
                this.processData()
                this.renderCharts()
            } catch (error) {
                this.$message.error(`获取数据失败: ${error.message || error}`)
                console.error('fetchBoardData error:', error)
            } finally {
                loadingInstance.close();
            }
        },

        // 处理数据
        processData() {
            if (!this.boardData) return

            console.log('boardData:', this.boardData)

            var gmvSummary = this.boardData.gmvSummary || {}
            var orderSummary = this.boardData.orderSummary || {}
            var commissionSummary = this.boardData.commissionSummary || {}

            // 处理KPI数据 - 从Summary对象中提取
            this.kpiData = {
                // totals
                gmv: Object.assign({ totalAmount: gmvSummary.totalAmount || 0 }, {
                    myAmount: gmvSummary.myAmount || 0,
                    subAmount: gmvSummary.subAmount || 0,
                    myPercentage: this.calculatePercentage(
                        gmvSummary.myAmount || 0,
                        gmvSummary.totalAmount || 0
                    ),
                    subPercentage: this.calculatePercentage(
                        gmvSummary.subAmount || 0,
                        gmvSummary.totalAmount || 0
                    )
                }),
                orderCount: Object.assign({ totalCount: orderSummary.totalCount || 0 }, {
                    myAmount: orderSummary.myCount || 0,
                    subAmount: orderSummary.subCount || 0,
                    myPercentage: this.calculatePercentage(
                        orderSummary.myCount || 0,
                        orderSummary.totalCount || 0
                    ),
                    subPercentage: this.calculatePercentage(
                        orderSummary.subCount || 0,
                        orderSummary.totalCount || 0
                    )
                }),
                commission: Object.assign({ totalAmount: commissionSummary.totalAmount || 0 }, {
                    myAmount: commissionSummary.myAmount || 0,
                    subAmount: commissionSummary.subAmount || 0,
                    myPercentage: this.calculatePercentage(
                        commissionSummary.myAmount || 0,
                        commissionSummary.totalAmount || 0
                    ),
                    subPercentage: this.calculatePercentage(
                        commissionSummary.subAmount || 0,
                        commissionSummary.totalAmount || 0
                    )
                })
            }

            console.log('kpiData:', this.kpiData)

            // 处理排行数据
            this.updateProductRanking()
            this.updateSubordinateRanking()

            // 绘制甜甜圈图
            this.$nextTick(function () {
                this.drawAllDonutCharts()
            }.bind(this))
        },

        calculatePercentage(value, total) {
            if (!total || total === 0) return 0
            return value / total
        },

        // 绘制甜甜圈图
        drawAllDonutCharts() {
            // GMV甜甜圈
            this.drawDonutChart('gmvMyChart', this.kpiData.gmv.myPercentage, '#FE2C65', '#B824F6', true)
            this.drawDonutChart('gmvSubChart', this.kpiData.gmv.subPercentage, '#FE2C65', '#B824F6', false)

            // 订单数甜甜圈
            this.drawDonutChart('orderMyChart', this.kpiData.orderCount.myPercentage, '#2E72FF', '#6295FF', true)
            this.drawDonutChart('orderSubChart', this.kpiData.orderCount.subPercentage, '#2E72FF', '#6295FF', false)

            // 佣金甜甜圈
            this.drawDonutChart('commissionMyChart', this.kpiData.commission.myPercentage, '#704FD4', '#9376EC', true)
            this.drawDonutChart('commissionSubChart', this.kpiData.commission.subPercentage, '#704FD4', '#9376EC', false)
        },

        drawDonutChart(canvasRef, percentage, startColor, endColor, isClockwise) {
            var canvas = this.$refs[canvasRef]
            if (!canvas) return

            var size = 160
            var dpr = window.devicePixelRatio || 1
            canvas.width = size * dpr
            canvas.height = size * dpr
            canvas.style.width = size + 'px'
            canvas.style.height = size + 'px'

            var ctx = canvas.getContext('2d')
            // scale context so we can draw in CSS pixels
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

            // clear
            ctx.clearRect(0, 0, size, size)

            var lineWidth = 22 // ring width
            var radius = (size - lineWidth) / 2
            var center = size / 2

            // draw background circle
            ctx.beginPath()
            ctx.lineWidth = lineWidth
            ctx.strokeStyle = '#E8E8E8'
            ctx.lineCap = 'round'
            ctx.arc(center, center, radius, 0, Math.PI * 2)
            ctx.stroke()

            // create gradient
            var gradient = ctx.createLinearGradient(center - radius, center - radius, center + radius, center + radius)
            gradient.addColorStop(0, startColor)
            gradient.addColorStop(1, endColor)

            // clamp percentage
            var p = percentage || 0
            if (p < 0) p = 0
            if (p > 1) p = 1

            // draw progress arc
            var startAngle = -Math.PI / 2
            var endAngle = isClockwise ? (startAngle + Math.PI * 2 * p) : (startAngle - Math.PI * 2 * p)
            ctx.beginPath()
            ctx.lineWidth = lineWidth
            ctx.strokeStyle = gradient
            ctx.lineCap = 'round'
            // anticlockwise param: true means draw counter-clockwise
            var anticlockwise = !isClockwise
            ctx.arc(center, center, radius, startAngle, endAngle, anticlockwise)
            ctx.stroke()

            // draw centered percentage text
            var percentText = (Math.round(p * 1000) / 10).toFixed(1) + '%'
            ctx.font = '700 30px Arial'
            ctx.fillStyle = endColor
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(percentText, center, center)
        },

        // 渲染图表
        renderCharts() {
            // 处理GMV趋势
            this.renderGmvChart()
            this.renderOrderChart()
            this.renderCommissionChart()
        },

        renderGmvChart() {
            var trendData = this.boardData.gmvTrend || []
            var chartData = this.processGmvChartData(trendData)
            // 主色：紫红
            this.renderEcharts('gmvChart', chartData, { colors: ['#8A2BE2', '#C44FC7', '#F7C8F0'], area: 'rgba(196,79,199,0.08)' }, '跟单订单金额(元)')
        },

        renderOrderChart() {
            var trendData = this.boardData.orderTrend || []
            var chartData = this.processOrderChartData(trendData)
            // 主色：蓝
            this.renderEcharts('orderChart', chartData, { colors: ['#0B66C3', '#4A90E2', '#BFE6FF'], area: 'rgba(74,144,226,0.08)' }, '跟单订单数(笔)')
        },

        renderCommissionChart() {
            var trendData = this.boardData.commissionTrend || []
            var chartData = this.processCommissionChartData(trendData)
            // 主色：紫
            this.renderEcharts('commissionChart', chartData, { colors: ['#5D33A8', '#8A2BE2', '#DEC1FF'], area: 'rgba(138,43,226,0.08)' }, '跟单订单佣金(元)')
        },

        processGmvChartData(trendData) {
            if (!trendData || !Array.isArray(trendData)) return { xAxisData: [], seriesData: [] }

            // decide format: if selected range >= 7 days use 'MM-DD', else use 'HH:mm'
            var useMonthDay = false
            try {
                if (this.dateRange && this.dateRange.length === 2) {
                    var s = dayjs(this.dateRange[0])
                    var e = dayjs(this.dateRange[1])
                    useMonthDay = e.diff(s, 'day') >= 7
                }
            } catch (e) {
                useMonthDay = false
            }

            var xAxisData = []
            var totalSeries = []
            var mySeries = []
            var subSeries = []
            var timeRanges = []

            trendData.forEach(function (item) {
                var startTime = item.startTime || ''
                var timeStr = ''
                if (startTime) {
                    timeStr = useMonthDay ? dayjs(startTime).format('MM-DD') : dayjs(startTime).format('HH:mm')
                }
                xAxisData.push(timeStr)
                // store raw time ranges for tooltip: prefer startTime/endTime if available
                timeRanges.push({ start: item.startTime || item.start || '', end: item.endTime || item.end || '' })

                totalSeries.push(item.totalAmount || 0)
                mySeries.push(item.myAmount || 0)
                subSeries.push(item.subAmount || 0)
            }.bind(this))

            return {
                xAxisData: xAxisData,
                timeRanges: timeRanges,
                seriesData: [
                    { name: '总', data: totalSeries },
                    { name: '我的', data: mySeries },
                    { name: '下级', data: subSeries }
                ]
            }
        },

        processOrderChartData(trendData) {
            if (!trendData || !Array.isArray(trendData)) return { xAxisData: [], seriesData: [] }

            var useMonthDay = false
            try {
                if (this.dateRange && this.dateRange.length === 2) {
                    var s = dayjs(this.dateRange[0])
                    var e = dayjs(this.dateRange[1])
                    useMonthDay = e.diff(s, 'day') >= 7
                }
            } catch (e) {
                useMonthDay = false
            }

            var xAxisData = []
            var totalSeries = []
            var mySeries = []
            var subSeries = []
            var timeRanges = []

            trendData.forEach(function (item) {
                var startTime = item.startTime || ''
                var timeStr = ''
                if (startTime) {
                    timeStr = useMonthDay ? dayjs(startTime).format('MM-DD') : dayjs(startTime).format('HH:mm')
                }
                xAxisData.push(timeStr)
                timeRanges.push({ start: item.startTime || item.start || '', end: item.endTime || item.end || '' })

                totalSeries.push(item.totalCount || 0)
                mySeries.push(item.myCount || 0)
                subSeries.push(item.subCount || 0)
            }.bind(this))

            return {
                xAxisData: xAxisData,
                timeRanges: timeRanges,
                seriesData: [
                    { name: '总', data: totalSeries },
                    { name: '我的', data: mySeries },
                    { name: '下级', data: subSeries }
                ]
            }
        },

        processCommissionChartData(trendData) {
            if (!trendData || !Array.isArray(trendData)) return { xAxisData: [], seriesData: [] }

            var useMonthDay = false
            try {
                if (this.dateRange && this.dateRange.length === 2) {
                    var s = dayjs(this.dateRange[0])
                    var e = dayjs(this.dateRange[1])
                    useMonthDay = e.diff(s, 'day') >= 7
                }
            } catch (e) {
                useMonthDay = false
            }

            var xAxisData = []
            var totalSeries = []
            var mySeries = []
            var subSeries = []
            var timeRanges = []

            trendData.forEach(function (item) {
                var startTime = item.startTime || ''
                var timeStr = ''
                if (startTime) {
                    timeStr = useMonthDay ? dayjs(startTime).format('MM-DD') : dayjs(startTime).format('HH:mm')
                }
                xAxisData.push(timeStr)
                timeRanges.push({ start: item.startTime || item.start || '', end: item.endTime || item.end || '' })

                totalSeries.push(item.totalAmount || 0)
                mySeries.push(item.myAmount || 0)
                subSeries.push(item.subAmount || 0)
            }.bind(this))

            return {
                xAxisData: xAxisData,
                timeRanges: timeRanges,
                seriesData: [
                    { name: '总', data: totalSeries },
                    { name: '我的', data: mySeries },
                    { name: '下级', data: subSeries }
                ]
            }
        },

        renderEcharts(chartRef, chartData, opts, yAxisName) {
            const dom = this.$refs[chartRef]
            if (!dom) return

            let instance = this.chartInstances[chartRef]
            if (!instance) {
                instance = echarts.init(dom)
                this.chartInstances[chartRef] = instance
            }

            var colors = (opts && opts.colors) || ['#FF4444', '#4A90E2', '#4CAF50']
            const option = {
                grid: { left: '3%', right: '4%', bottom: '3%', top: '20%', containLabel: true },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' },
                    formatter: (params) => {
                        // params is an array of series points at the same x index
                        if (!params || !params.length) return ''
                        const idx = params[0].dataIndex
                        // chartData may include timeRanges
                        const timeRanges = chartData.timeRanges || []
                        const tr = timeRanges[idx] || {}
                        const fmt = (t) => {
                            if (!t) return ''
                            return dayjs(t).format('HH:mm:ss')
                        }
                        let timeLabel = params[0].axisValue
                        if (tr.start && tr.end) {
                            timeLabel = `${fmt(tr.start)} - ${fmt(tr.end)}`
                        } else if (tr.start) {
                            timeLabel = fmt(tr.start)
                        } else if (tr.end) {
                            timeLabel = fmt(tr.end)
                        }

                        // build tooltip lines: first line is time range, subsequent lines are series
                        let lines = []
                        lines.push(timeLabel)
                        params.forEach(p => {
                            lines.push(`${p.marker} ${p.seriesName}: ${p.value}`)
                        })
                        return lines.join('<br/>')
                    }
                },
                // hide built-in legend (we render custom legend in header) but keep selected state
                legend: { show: false, selected: { '总': this.getSelectedState(chartRef, 'total'), '我的': this.getSelectedState(chartRef, 'my'), '下级': this.getSelectedState(chartRef, 'sub') } },
                xAxis: { type: 'category', data: chartData.xAxisData, axisLabel: { rotate: chartData.seriesData && chartData.seriesData[0].data.length > 9 ? 45 : 0, fontSize: 12, color: '#666', interval: chartData.seriesData && chartData.seriesData[0].data.length > 20 ? 1 : 0, } },
                yAxis: { type: 'value', name: yAxisName || '', nameLocation: 'end', nameRotate: 0, nameGap: 10, nameTextStyle: { fontSize: 12, color: '#666' } },
                series: [
                    {
                        name: '总',
                        data: chartData.seriesData && chartData.seriesData[0] ? chartData.seriesData[0].data : [],
                        type: 'line',
                        smooth: true,
                        lineStyle: { color: colors[0] || '#FF4444', width: 2 }
                    },
                    {
                        name: '我的',
                        data: chartData.seriesData && chartData.seriesData[1] ? chartData.seriesData[1].data : [],
                        type: 'line',
                        smooth: true,
                        lineStyle: { color: colors[1] || '#4A90E2', width: 2 }
                    },
                    {
                        name: '下级',
                        data: chartData.seriesData && chartData.seriesData[2] ? chartData.seriesData[2].data : [],
                        type: 'line',
                        smooth: true,
                        lineStyle: { color: colors[2] || '#4CAF50', width: 2 }
                    }
                ]
            }

            instance.setOption(option)
        },

        getSelectedState(chartRef, key) {
            if (chartRef === 'gmvChart') {
                if (key === 'total') return !!this.chartLegend.gmvTotal
                if (key === 'my') return !!this.chartLegend.gmvMy
                if (key === 'sub') return !!this.chartLegend.gmvSub
            }
            if (chartRef === 'orderChart') {
                if (key === 'total') return !!this.chartLegend.orderTotal
                if (key === 'my') return !!this.chartLegend.orderMy
                if (key === 'sub') return !!this.chartLegend.orderSub
            }
            if (chartRef === 'commissionChart') {
                if (key === 'total') return !!this.chartLegend.commissionTotal
                if (key === 'my') return !!this.chartLegend.commissionMy
                if (key === 'sub') return !!this.chartLegend.commissionSub
            }
            return true
        },

        // 切换图表系列显示（通过自定义 legend 点击触发）
        toggleSeries(chartRef, seriesName) {
            var instance = this.chartInstances[chartRef]
            if (!instance) {
                if (chartRef === 'gmvChart') {
                    if (seriesName === '总') this.chartLegend.gmvTotal = !this.chartLegend.gmvTotal
                    if (seriesName === '我的') this.chartLegend.gmvMy = !this.chartLegend.gmvMy
                    if (seriesName === '下级') this.chartLegend.gmvSub = !this.chartLegend.gmvSub
                }
                if (chartRef === 'orderChart') {
                    if (seriesName === '总') this.chartLegend.orderTotal = !this.chartLegend.orderTotal
                    if (seriesName === '我的') this.chartLegend.orderMy = !this.chartLegend.orderMy
                    if (seriesName === '下级') this.chartLegend.orderSub = !this.chartLegend.orderSub
                }
                if (chartRef === 'commissionChart') {
                    if (seriesName === '总') this.chartLegend.commissionTotal = !this.chartLegend.commissionTotal
                    if (seriesName === '我的') this.chartLegend.commissionMy = !this.chartLegend.commissionMy
                    if (seriesName === '下级') this.chartLegend.commissionSub = !this.chartLegend.commissionSub
                }
                return
            }

            instance.dispatchAction({ type: 'legendToggleSelect', name: seriesName })

            if (chartRef === 'gmvChart') {
                if (seriesName === '总') this.chartLegend.gmvTotal = !this.chartLegend.gmvTotal
                if (seriesName === '我的') this.chartLegend.gmvMy = !this.chartLegend.gmvMy
                if (seriesName === '下级') this.chartLegend.gmvSub = !this.chartLegend.gmvSub
            }
            if (chartRef === 'orderChart') {
                if (seriesName === '总') this.chartLegend.orderTotal = !this.chartLegend.orderTotal
                if (seriesName === '我的') this.chartLegend.orderMy = !this.chartLegend.orderMy
                if (seriesName === '下级') this.chartLegend.orderSub = !this.chartLegend.orderSub
            }
            if (chartRef === 'commissionChart') {
                if (seriesName === '总') this.chartLegend.commissionTotal = !this.chartLegend.commissionTotal
                if (seriesName === '我的') this.chartLegend.commissionMy = !this.chartLegend.commissionMy
                if (seriesName === '下级') this.chartLegend.commissionSub = !this.chartLegend.commissionSub
            }
        },

        // 商品排行
        onProductRankTypeChange() {
            this.updateProductRanking()
        },

        updateProductRanking() {
            var rankingType = this.productRankType || 'total' // total/my/sub
            var metric = this.productMetricType || 'gmv' // gmv / orderCount / commission

            // build key names according to API naming convention
            // metric mapping: orderCount -> OrderCount, gmv -> Gmv, commission -> Commission
            var metricSuffix = ''
            if (metric === 'gmv') metricSuffix = 'Gmv'
            else if (metric === 'orderCount') metricSuffix = 'OrderCount'
            else metricSuffix = 'Commission'

            var keyName = rankingType + 'ProductRankBy' + metricSuffix
            var data = (this.boardData && this.boardData[keyName]) || []

            this.productRankingData = data.slice(0, 10).map(function (item) {
                return {
                    goodsName: item.goodsName || item.title || '',
                    goodsThumbnailUrl: item.goodsThumbnailUrl || item.goodsThumbnail || '',
                    shopName: item.shopName || item.storeName || '',
                    gmv: item.gmv || item.totalAmount || 0,
                    orderCount: item.orderCount || item.totalCount || 0,
                    commissionAmount: item.commissionAmount || item.totalCommission || 0
                }
            }.bind(this))
        },

        onSubordinateSortChange() {
            this.updateSubordinateRanking()
        },

        updateSubordinateRanking() {
            var metric = this.subordinateSortBy || 'gmv'
            var metricSuffix = ''
            if (metric === 'gmv') metricSuffix = 'Gmv'
            else if (metric === 'orderCount') metricSuffix = 'OrderCount'
            else metricSuffix = 'Commission'

            var keyName = 'subordinateRankBy' + metricSuffix
            var data = (this.boardData && this.boardData[keyName]) || []

            this.subordinateRankingData = data.slice(0, 10).map(function (item) {
                return {
                    accountId: item.accountId,
                    mobilePhone: item.mobilePhone,
                    nickName: item.nickName || item.accountName || '',
                    gmv: item.gmv || item.totalAmount || 0,
                    orderCount: item.orderCount || item.totalCount || 0,
                    commissionAmount: item.commissionAmount || item.totalCommission || 0
                }
            }.bind(this))
        },

        // 表格排序处理（客户端排序）
        handleSortChange(type, event) {
            if (!event || !event.prop) return
            var prop = event.prop
            var order = event.order // 'ascending' / 'descending' / null

            var metricCols = ['gmv', 'orderCount', 'commissionAmount']

            if (type === 'product') {
                if (metricCols.indexOf(prop) !== -1) {
                    var metricKey = prop === 'commissionAmount' ? 'commission' : prop
                    this.productMetricType = metricKey
                    this.updateProductRanking()
                }
            } else if (type === 'subordinate') {
                if (metricCols.indexOf(prop) !== -1) {
                    var subMetric = prop === 'commissionAmount' ? 'commission' : prop
                    this.subordinateSortBy = subMetric
                    this.updateSubordinateRanking()
                }
            }
        },

        // custom header click: product table single-click descending sort
        onProductHeaderClick(prop) {
            if (!prop) return
            // set active state reactively and clear subordinate active
            this.$set(this.productSortActive, 'prop', prop)
            this.$set(this.productSortActive, 'order', 'descending')
            this.$set(this.subordinateSortActive, 'prop', '')
            console.log('onProductHeaderClick -> productSortActive', this.productSortActive)

            // if metric column clicked, map to metric type
            var metricCols = ['gmv', 'orderCount', 'commissionAmount']
            if (metricCols.indexOf(prop) !== -1) {
                var metricKey = prop === 'commissionAmount' ? 'commission' : prop
                this.productMetricType = metricKey
                // update productRankingData from boardData and then sort descending
                this.updateProductRanking()
                // sort the productRankingData in-place descending
                // this.sortTableData(this.productRankingData, prop, 'descending')
            }
            this.$forceUpdate()
        },

        // custom header click: subordinate table single-click descending sort
        onSubordinateHeaderClick(prop) {
            if (!prop) return
            // set active state reactively and clear product active
            this.$set(this.subordinateSortActive, 'prop', prop)
            this.$set(this.subordinateSortActive, 'order', 'descending')
            this.$set(this.productSortActive, 'prop', '')
            console.log('onSubordinateHeaderClick -> subordinateSortActive', this.subordinateSortActive)

            var metricCols = ['gmv', 'orderCount', 'commissionAmount']
            if (metricCols.indexOf(prop) !== -1) {
                var subMetric = prop === 'commissionAmount' ? 'commission' : prop
                this.subordinateSortBy = subMetric
                this.updateSubordinateRanking()
                // this.sortTableData(this.subordinateRankingData, prop, 'descending')
            }
            this.$forceUpdate()
        },

        isProductActive(prop) {
            return this.productSortActive && this.productSortActive.prop === prop
        },

        isSubActive(prop) {
            return this.subordinateSortActive && this.subordinateSortActive.prop === prop
        },

        getProductIcon(prop) {
            try {
                const active = this.productSortActive && this.productSortActive.prop === prop
                // webpack will resolve these requires to correct URLs
                return active ? require('./image/downa.png') : require('./image/down.png')
            } catch (e) {
                console.warn('getProductIcon require failed', e)
                return active ? '/src/page/component/routes/index/bd-manage/overview/image/downa.png' : '/src/page/component/routes/index/bd-manage/overview/image/down.png'
            }
        },

        getSubIcon(prop) {
            try {
                const active = this.subordinateSortActive && this.subordinateSortActive.prop === prop
                return active ? require('./image/downa.png') : require('./image/down.png')
            } catch (e) {
                console.warn('getSubIcon require failed', e)
                return active ? '/src/page/component/routes/index/bd-manage/overview/image/downa.png' : '/src/page/component/routes/index/bd-manage/overview/image/down.png'
            }
        },

        sortTableData(arr, prop, order) {
            if (!Array.isArray(arr)) return
            if (!order) return // no sorting
            arr.sort(function (a, b) {
                var av = a[prop]
                var bv = b[prop]
                // try numeric
                var na = parseFloat(av)
                var nb = parseFloat(bv)
                if (!isNaN(na) && !isNaN(nb)) {
                    return order === 'ascending' ? na - nb : nb - na
                }
                // fallback string compare
                if (av == null) return 1
                if (bv == null) return -1
                if (order === 'ascending') return String(av).localeCompare(String(bv))
                return String(bv).localeCompare(String(av))
            })
            // force Vue to update (in case array mutated)
            // eslint-disable-next-line
            this.$set(this, arr === this.productRankingData ? 'productRankingData' : 'subordinateRankingData', arr.slice())
        },

        // 初始化
        async initData() {
            const range = this.getQuickDateRange('today')
            this.dateRange = range
            await this.fetchBoardData(range[0], range[1])
        }
    },

    created() {
        this.initData()
    },

    beforeDestroy() {
        // 销毁图表实例
        Object.values(this.chartInstances).forEach(instance => {
            instance && instance.dispose()
        })
    }
}
</script>

<style scoped lang="less">
.overview-page {
    padding: 20px;
    background: #f5f7fa;
    min-height: 100vh;
}

.page-title {
    margin-bottom: 20px;

    h1 {
        font-size: 24px;
        font-weight: 600;
        color: #333;
        margin: 0;
    }
}

.shadow-card {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(17, 24, 39, 0.06);
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 15px;
}

// 时间筛选区
.filter-section {
    position: relative;
    padding: 12px 18px;

    .filter-inner {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
    }

    .filter-left {
        display: flex;
        align-items: center;
    }

    .filter-header {
        font-size: 16px;
        font-weight: 600;
        color: #333;
    }

    .filter-right {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .date-input-group {
        min-width: 300px;
    }

    .loading-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
    }
}

// KPI卡片区
.kpi-section {
    //   margin-bottom: 20px;

    /* totals-row 已移除（模板未使用），相关样式保持在需要时再添加 */

    .kpi-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;

        @media (max-width: 1200px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }
    }

    .kpi-card {
        .card-top {
            text-align: center;
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 15px;
        }

        .card-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 180px;
            padding: 10px;
        }

        .kpi-flex-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 0;
            width: 100%;
        }

        .kpi-block {
            flex: 1;
            text-align: center
        }

        .kpi-block-right {
            text-align: center
        }

        .kpi-inline {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-bottom: 20px;
            font-size: 16px;
            color: #666;
        }

        .kpi-sub-label {
            font-weight: 500
        }

        .kpi-sub-value {
            font-weight: 700;
            color: #111
        }

        .donut-chart {
            position: relative;
            width: 160px;
            height: 160px;
            margin: 0 auto
        }

        .donut-label {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            font-size: 14px;
            font-weight: 700;
            color: #333;
            pointer-events: none;
        }

        /* hide DOM label because percentage is drawn on canvas for crisp rendering */
        .donut-label {
            display: none
        }
    }

    /* specific card colors */
    .gmv-card .card-top {
        color: #FE2C65
    }

    .order-card .card-top {
        color: #3D7CFF
    }

    .commission-card .card-top {
        color: #704FD4
    }
}

// 趋势图表区
.chart-section {
    //   margin-bottom: 20px;

    .chart-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
        gap: 16px;

        @media (max-width: 1200px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }
    }

    .chart-card {
        .chart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: -10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #f5f6f8;

            h3 {
                margin: 0;
                font-size: 15px;
                font-weight: 700;
                color: #222;
            }

            .chart-legend {
                display: flex;
                gap: 10px;
                align-items: center;
            }

            .chart-legend .legend-item {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                cursor: pointer;
                color: #666;
                font-size: 13px;
            }

            .chart-legend .legend-item .dot {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                display: inline-block;
                vertical-align: middle;
            }

            .chart-legend .legend-item.active {
                color: #111
            }
        }

        .chart-container {
            height: 280px;
            overflow: hidden;

            .echarts {
                width: 100%;
                height: 100%;
            }
        }
    }
}

/* 两个排行左右并列 */
.rankings-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
}

.ranking-left,
.ranking-right {
    padding: 12px;
    /* prevent internal horizontal scroll */
    overflow: hidden;
}

/* Ensure ranking headers center their content horizontally */
.ranking-header {
    margin: 5px 0 15px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    /* keep spacing between title and controls when present */
}

@media (max-width: 1200px) {
    .rankings-wrapper {
        grid-template-columns: 1fr
    }
}

/* tighten table column padding and prevent text wrapping where possible */
.ranking-left .el-table,
.ranking-right .el-table {
    table-layout: fixed;
}

.ranking-left .el-table th,
.ranking-right .el-table th,
.ranking-left .el-table td,
.ranking-right .el-table td {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.ranking-left .el-table .product-img {
    width: 40px;
    height: 40px
}

/* Set table row height: remove vertical padding and use line-height to get 50px rows */
.ranking-left .el-table td,
.ranking-right .el-table td {
    padding-top: 0;
    padding-bottom: 0;
    line-height: 30px; /* drive row height */
}

    /* custom sortable header */
    .sortable-header {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        color: #666;
        user-select: none;
    }

    .sortable-header .sort-icon {
        display: inline-block;
        transition: filter 0.15s ease, transform 0.15s ease;
        transform: translateY(0);
        filter: grayscale(100%) brightness(0.9);
    }

    /* active text should be blue */
    .sortable-header .sort-active {
        color: #2e72ff !important;
        font-weight: 700;
    }

    /* when using active image, no filter needed; keep normal for non-active */
    .sortable-header .sort-icon {
        filter: none;
    }

/* Ensure Element table cells (.cell) are also forced to the same spacing */
.ranking-left ::v-deep .el-table .cell,
.ranking-right ::v-deep .el-table .cell {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 30px !important;
    height: 30px !important;
}

/* Prevent table header text wrapping and show ellipsis for overflow */
.ranking-left .el-table th .cell,
.ranking-right .el-table th .cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

::v-deep .el-table {
    .el-table__header {
        background: #f5f5f5;
    }

    .el-table__row {
        &:hover>td {
            background-color: #f5f7fa !important;
        }
    }
    .first-row {
    background: linear-gradient( 90deg, #FFF7EF 0%, #fff 100%);
  }
  .second-row {
    background: linear-gradient( 90deg, #EAF5FE 0%, #fff 100%);
  }
  .third-row {
        background: linear-gradient( 90deg, #FEF4F0 0%, #fff 100%);
    }
}
</style>
